import * as React from "react";

import {
  getCoreRowModel,
  Table,
  flexRender,
  useReactTable,
  SortingState,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnDef,
  TableOptions,
} from "@tanstack/react-table";
import { BsSortDown, BsSortUp } from "react-icons/bs";
import { Button } from "@components/ui/button";
import { Accordion, AccordionTrigger } from "@components/ui/accordion";
import { AccordionItem } from "@radix-ui/react-accordion";

export type { ColumnDef };

type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export function useTable<T>(props: WithOptional<TableOptions<T>, 'getCoreRowModel'>) {
  const [sorting, setSorting] = React.useState<SortingState>(props.state?.sorting ?? []);
  const table = useReactTable({
    state: {
      ...props.state,
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel?.(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    ...props,
  });
  return {
    table,
    sorting,
  };
}

type Props<T> = {
  table: Table<T>;
  totalRows: number;
  maxHeight?: string;
  disableFooter?: boolean;
} & (
    | {
      manualPaginationProps?: undefined;
    }
    | {
      manualPaginationProps: {
        rowsPerPage: number;
        currentPageIndex: number;
        totalPages: number;
        onRowsPerPageChange: (newPerPage: number, page: number) => void;
        onPageIndexChange: (val: number) => void;
      };
    }
  );

function CustomTable<T>({
  table,
  totalRows,
  manualPaginationProps,
  disableFooter,
  maxHeight,
}: Props<T>) {
  const baseOptions = [50, 100, 200, 300, 500];

  const options = manualPaginationProps
    ? Array.from(new Set([...baseOptions, manualPaginationProps.rowsPerPage]))
    : Array.from(
      new Set([...baseOptions, table.getState().pagination.pageSize]),
    );

  const pageSize = manualPaginationProps
    ? manualPaginationProps.rowsPerPage
    : table.getState().pagination.pageSize;

  const gotoFirstPage = () => {
    if (manualPaginationProps) {
      manualPaginationProps.onPageIndexChange(0);
    } else {
      table.setPageIndex(0);
    }
  };

  const gotoLastPage = () => {
    if (manualPaginationProps) {
      manualPaginationProps.onPageIndexChange(
        manualPaginationProps.totalPages - 1,
      );
    } else {
      table.setPageIndex(table.getPageCount() - 1);
    }
  };

  const gotoNextPage = () => {
    if (manualPaginationProps) {
      manualPaginationProps.onPageIndexChange(
        manualPaginationProps.currentPageIndex + 1,
      );
    } else {
      table.nextPage();
    }
  };
  const gotoPreviousPage = () => {
    if (manualPaginationProps) {
      manualPaginationProps.onPageIndexChange(
        manualPaginationProps.currentPageIndex - 1,
      );
    } else {
      table.previousPage();
    }
  };

  const getPageInfo = () => {
    const _pageIndex =
      manualPaginationProps?.currentPageIndex ??
      table.getState().pagination.pageIndex;

    return `${pageSize * _pageIndex + 1}-${Math.min(
      pageSize * (1 + _pageIndex) - 1,
      totalRows,
    )} of ${totalRows}`;
  };

  function isButtonDisabled(button: "next" | "previous" | "first" | "last") {
    if (!manualPaginationProps) {
      switch (button) {
        case "next":
          return !table.getCanNextPage();
        case "previous":
          return !table.getCanPreviousPage();
        case "last":
          return !table.getCanNextPage();
        case "first":
          return !table.getCanPreviousPage();
      }
    }
    switch (button) {
      case "next":
        return !(
          manualPaginationProps.currentPageIndex <
          manualPaginationProps.totalPages - 1
        );
      case "previous":
        return !(manualPaginationProps.currentPageIndex > 0);
      case "last":
        return (
          manualPaginationProps.currentPageIndex ===
          manualPaginationProps.totalPages - 1
        );
      case "first":
        return manualPaginationProps.currentPageIndex === 0;
    }
  }

  return (
    <Accordion type="single" collapsible>
      <div className="flex h-full w-full flex-col pt-2">
        <div
          className="w-full max-w-full flex-1 overflow-x-auto"
          style={{
            maxHeight: maxHeight ?? "90vh",
          }}
        >
          <table
            style={{
              width: "100%",
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              style: {
                                cursor: header.column.getCanSort()
                                  ? "pointer"
                                  : "",
                                textAlign: "start",
                                userSelect: "none",
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                              },
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            <div
                              style={{
                                width: "1rem",
                              }}
                            >
                              {(
                                {
                                  desc: <BsSortDown />,
                                  asc: <BsSortUp />,
                                } as Record<string, React.ReactNode>
                              )[header.column.getIsSorted() as string] ?? null}
                            </div>
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <AccordionItem value={row.id} key={row.id} asChild>
                  <tr>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>
                        {
                          cell.getIsGrouped() ? (
                            <button onClick={row.getToggleExpandedHandler()}>
                              <AccordionTrigger>
                                {
                                  flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )
                                }
                              </AccordionTrigger>
                            </button>
                          ) :
                            cell.getIsAggregated() ? (
                              // If the cell is aggregated, use the Aggregated
                              // renderer for cell
                              flexRender(
                                cell.column.columnDef.aggregatedCell ??
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            ) : cell.getIsPlaceholder() ? null : ( // For cells with repeated values, render null
                              // Otherwise, just render the regular cell
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            )
                        }
                      </td>
                    ))}
                  </tr>
                </AccordionItem>
              ))}
            </tbody>
          </table>
        </div>
        {!disableFooter && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontSize: "0.875rem",
              paddingBottom: "4px",
            }}
          >
            <div
              style={{
                display: "flex",
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={gotoFirstPage}
                disabled={isButtonDisabled("first")}
              >
                {"<<"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={gotoPreviousPage}
                disabled={isButtonDisabled("previous")}
              >
                {"<"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={gotoNextPage}
                disabled={isButtonDisabled("next")}
              >
                {">"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={gotoLastPage}
                disabled={isButtonDisabled("last")}
              >
                {">>"}
              </Button>
            </div>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                fontSize: "0.875rem",
              }}
            >
              <p>{getPageInfo()}</p>
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              Rows per page:
              <select
                value={`${pageSize}`}
                onChange={(e) => {
                  if (manualPaginationProps) {
                    manualPaginationProps.onRowsPerPageChange(
                      Number(e.target.value),
                      manualPaginationProps.currentPageIndex,
                    );
                  } else {
                    table.setPageSize(Number(e.target.value));
                  }
                }}
              >
                {options.map((val) => (
                  <option value={val} key={`table_${val}`}>
                    {val}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </Accordion>
  );
}

export default CustomTable;
